import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useKitTemplatesList,
  useDeleteKitTemplate,
} from '../../hooks/useKitTemplates';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { ApiError } from '../../lib/api';

/**
 * Admin/coordinator list of kit templates. One row per position with
 * a per-station tally of expected items. Editable up to the day before
 * per CLAUDE.md 6.1.
 */
export function KitTemplatesList() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const list = useKitTemplatesList();
  const remove = useDeleteKitTemplate();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; position: string } | null>(
    null,
  );

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  const allowed = user.roles.includes('admin') || user.roles.includes('coordinator');
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to manage kit templates.
      </main>
    );
  }

  async function performDelete() {
    if (!deleteTarget) return;
    try {
      await remove.mutateAsync(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.position}"`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'delete failed');
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
              <FileText className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-accent">
                Admin
              </div>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                Kit templates
              </h1>
              <p className="mt-1 text-sm text-fg-muted">
                One template per role or role category. A person gets their
                category&apos;s baseline merged with their role&apos;s template.
                Editable up to the day before mobilisation.
              </p>
            </div>
          </div>
          <Link to="/admin/kit-templates/new">
            <Button variant="primary" size="md" iconLeft={<Plus className="h-4 w-4" />}>
              New template
            </Button>
          </Link>
        </header>

        <div className="mt-6 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {list.isLoading ? (
            <SkeletonList rows={4} />
          ) : list.error ? (
            <EmptyState
              icon={<AlertCircle className="h-6 w-6" />}
              title="Couldn't load templates"
              description={(list.error as Error).message}
            />
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-6 w-6" />}
              title="No templates yet"
              description="Create one per role category (the shared baseline) or per role so staff get the right checklist on the day."
              action={
                <Link to="/admin/kit-templates/new">
                  <Button variant="primary" iconLeft={<Plus className="h-4 w-4" />}>
                    Create the first template
                  </Button>
                </Link>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
                  <tr>
                    <th className="px-4 py-2.5">Applies to</th>
                    <th className="px-4 py-2.5">Items</th>
                    <th className="px-4 py-2.5">By station</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((tpl) => {
                    const byStation: Record<string, number> = {};
                    for (const it of tpl.items) {
                      byStation[it.station] =
                        (byStation[it.station] ?? 0) + (it.quantity ?? 1);
                    }
                    return (
                      <tr
                        key={tpl.id}
                        className="border-t border-line/10 transition-colors hover:bg-surface-2/50"
                      >
                        <td className="px-4 py-2.5 font-medium">
                          <span className="inline-flex items-center gap-2">
                            {tpl.position}
                            <Pill tone={tpl.category_id ? 'accent' : 'neutral'} size="sm">
                              {tpl.category_id ? 'Category' : 'Role'}
                            </Pill>
                          </span>
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-fg-muted">
                          {tpl.items.length}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(byStation).length === 0 ? (
                              <span className="text-xs text-fg-dim">none</span>
                            ) : (
                              Object.entries(byStation).map(([s, n]) => (
                                <span
                                  key={s}
                                  className="inline-flex items-center gap-1 rounded bg-surface-2 px-2 py-0.5 text-xs font-medium text-fg-muted"
                                >
                                  <span>{s}</span>
                                  <span className="tabular-nums text-fg-muted">·</span>
                                  <span className="tabular-nums">{n}</span>
                                </span>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="inline-flex gap-1">
                            <Link to={`/admin/kit-templates/${tpl.id}`}>
                              <Button
                                variant="secondary"
                                size="sm"
                                iconLeft={<Pencil className="h-3.5 w-3.5" />}
                              >
                                Edit
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              iconLeft={<Trash2 className="h-3.5 w-3.5" />}
                              onClick={() =>
                                setDeleteTarget({ id: tpl.id, position: tpl.position })
                              }
                              className="text-danger-soft-fg hover:bg-danger-soft/15"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        tone="destructive"
        title={`Delete the "${deleteTarget?.position ?? ''}" template?`}
        message="This removes the position's expected-kit checklist. Past kit-issuance events for people in this role stay in the chain; only the template goes."
        confirmLabel="Delete template"
        cancelLabel="Keep"
        onConfirm={performDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </main>
  );
}
